import { useState, useEffect, useRef, ReactElement } from 'react';
import * as pbi from 'powerbi-client';
import {
    IPowerBIReportEmbedTokenResponse,
    PowerBIReportsType,
    powerBiService
} from "../../../services/api/powerbi-service";
import styles from './powerbi-report.module.scss';

interface PowerBIReportProps {
    reportType: PowerBIReportsType;
    isDashboard?: boolean;
    className?: string;
    onReportLoaded?: () => void;
    onReportError?: (error: any) => void;
}

export const PowerBIReport = ({ reportType, isDashboard, className, onReportLoaded, onReportError }: PowerBIReportProps): ReactElement => {
    const reportContainerRef = useRef<HTMLDivElement | null>(null);
    const powerbiServiceRef = useRef<pbi.service.Service | null>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const container = reportContainerRef.current;
        if (!container) return;

        if (!powerbiServiceRef.current) {
            powerbiServiceRef.current = new pbi.service.Service(
                pbi.factories.hpmFactory,
                pbi.factories.wpmpFactory,
                pbi.factories.routerFactory
            );
        }
        const powerbi = powerbiServiceRef.current;

        const embedReport = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const embedResponse: IPowerBIReportEmbedTokenResponse = isDashboard ? await powerBiService.getDashboardEmbedToken(reportType)
                    : await powerBiService.getReportEmbedToken(reportType);
                const embedUrl = isDashboard ? `https://app.powerbi.com/dashboardEmbed?dashboardId=${embedResponse.reportId}`
                    : `https://app.powerbi.com/reportEmbed?reportId=${embedResponse.reportId}&ctid=${embedResponse.tenantId}&navContentPaneEnabled=false`;
                const embedConfig: pbi.models.IEmbedConfiguration = {
                    type: isDashboard ? 'dashboard' : 'report',
                    id: embedResponse.reportId,
                    embedUrl: embedUrl,
                    accessToken: embedResponse.embedToken,
                    tokenType: pbi.models.TokenType.Embed,
                    settings: {
                        panes: {
                            filters: {
                                expanded: false,
                                visible: false
                            },
                            pageNavigation: {
                                visible: false
                            }
                        }
                    },
                };

                const report = powerbi?.embed(container, embedConfig);

                report?.off('loaded');
                report?.on('loaded', () => {
                    console.log('Report BI is loaded successfully.');
                    setIsLoading(false);
                    onReportLoaded?.();
                });

                report?.off('error');
                report?.on('error', (event: any) => {
                    const errorDetail = event?.detail;
                    console.error(errorDetail);
                    setError('Report is not loaded.');
                    setIsLoading(false);
                    onReportError?.(errorDetail);
                });

            } catch (err) {
                console.error(err);
                setError('Report is not loaded.');
                setIsLoading(false);
                onReportError?.(err);
            }
        };

        powerbi?.reset(container);
        embedReport();

        return () => {
            powerbi?.reset(container);
        };
    }, [reportType, onReportLoaded, onReportError]);

    return (
        <div className={`${styles.powerbiContainer} ${className || ''}`}>
            {isLoading && <div className={styles.loader}>Loading...</div>}
            {error && !isLoading && <div className={styles.errorMessage}>{error}</div>}
            <div ref={reportContainerRef} className={styles.reportEmbedContainer} />
        </div>
    );
};
